import React, { useState, useEffect, useRef } from 'react';
import { updateReservation } from '../services/reservationService';
import './EditReservationModal.css';

const EditReservationModal = ({ reservation, onClose, onSuccess }) => {
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [formData, setFormData] = useState({
    date: '',
    time: '',
    guests: 2,
    specialOccasion: '',
    comments: '',
    allergies: [],
    dietaryPreference: [],
  });
  const [hasSpecialOccasion, setHasSpecialOccasion] = useState(false);
  const [allergiesExpanded, setAllergiesExpanded] = useState(false);
  const [dietaryExpanded, setDietaryExpanded] = useState(false);
  const [selectedMealPeriod, setSelectedMealPeriod] = useState(null);

  // Refs for scroll containers
  const guestsScrollRef = useRef(null);
  const timeSlotsScrollRef = useRef(null);
  const datesScrollRef = useRef(null);

  // Get restaurant from reservation
  const restaurant = reservation?.restaurant;

  // Normalize time to HH:MM format (remove seconds if present)
  const normalizeTime = (time) => {
    if (!time) return '';
    const parts = time.split(':');
    if (parts.length >= 2) {
      return `${parts[0].padStart(2, '0')}:${parts[1].padStart(2, '0')}`;
    }
    return time;
  };

  // Define the natural order for meal periods
  const MEAL_PERIOD_ORDER = {
    'breakfast': 1,
    'brunch': 2,
    'lunch': 3,
    'afternoon tea': 4,
    'happy hour': 5,
    'dinner': 6,
    'late night': 7,
  };

  // Get the sort order for a meal period label
  const getMealPeriodSortOrder = (label) => {
    const normalizedLabel = label?.toLowerCase()?.trim() || '';
    return MEAL_PERIOD_ORDER[normalizedLabel] ?? 99;
  };

  // Get available meal periods from restaurant configuration
  const getAvailableMealPeriods = () => {
    if (restaurant?.mealPeriodPolicy === 'none') {
      return [];
    }
    if (restaurant?.mealPeriods && Array.isArray(restaurant.mealPeriods) && restaurant.mealPeriods.length > 0) {
      // Sort meal periods by natural meal order (Breakfast → Lunch → Dinner)
      return [...restaurant.mealPeriods].sort((a, b) => {
        return getMealPeriodSortOrder(a.label) - getMealPeriodSortOrder(b.label);
      });
    }
    return [];
  };

  const availableMealPeriods = getAvailableMealPeriods();

  // Generate time slots based on meal period configuration
  const generateTimeSlots = (startTime, endTime, interval = 30) => {
    if (!startTime || !endTime) return [];

    const slots = [];
    const [startHour, startMin] = startTime.split(':').map(Number);
    const [endHour, endMin] = endTime.split(':').map(Number);

    let currentHour = startHour;
    let currentMin = startMin;

    while (
      currentHour < endHour ||
      (currentHour === endHour && currentMin < endMin)
    ) {
      const timeStr = `${String(currentHour).padStart(2, '0')}:${String(currentMin).padStart(2, '0')}`;
      slots.push(timeStr);

      currentMin += interval;
      if (currentMin >= 60) {
        currentHour += Math.floor(currentMin / 60);
        currentMin = currentMin % 60;
      }
    }

    return slots;
  };

  // Get time slots for the selected meal period
  const getTimeSlotsForMealPeriod = (mealPeriod) => {
    if (!mealPeriod || !mealPeriod.slots || mealPeriod.slots.length === 0) {
      return [];
    }

    const slotInterval = restaurant?.slotInterval || 30;
    const allSlots = [];

    mealPeriod.slots.forEach((slot) => {
      const slotsForRange = generateTimeSlots(slot.start, slot.end, slotInterval);
      allSlots.push(...slotsForRange);
    });

    return allSlots;
  };

  // Get time slots for continuous operation (no meal periods)
  const getContinuousTimeSlots = () => {
    const slotInterval = restaurant?.slotInterval || 30;
    const openingTime = restaurant?.openingTime || '09:00';
    const closingTime = restaurant?.closingTime || '22:00';

    return generateTimeSlots(openingTime, closingTime, slotInterval);
  };

  // Filter out past times if selected date is today
  const filterPastTimes = (slots) => {
    if (!formData.date) return slots;

    const selectedDate = new Date(formData.date);
    const today = new Date();

    selectedDate.setHours(0, 0, 0, 0);
    today.setHours(0, 0, 0, 0);

    if (selectedDate.getTime() !== today.getTime()) {
      return slots;
    }

    const now = new Date();
    const currentHour = now.getHours();
    const currentMin = now.getMinutes();

    return slots.filter(timeSlot => {
      const [slotHour, slotMin] = timeSlot.split(':').map(Number);
      if (slotHour > currentHour) return true;
      if (slotHour === currentHour && slotMin > currentMin) return true;
      return false;
    });
  };

  // Get all time slots based on meal period selection
  const getAllTimeSlots = () => {
    if (availableMealPeriods.length === 0) {
      return getContinuousTimeSlots();
    }
    return getTimeSlotsForMealPeriod(selectedMealPeriod);
  };

  const allTimeSlots = getAllTimeSlots();
  const timeSlots = filterPastTimes(allTimeSlots);

  // Format time to 12-hour format for display
  const formatTime = (time24) => {
    if (!time24) return '';
    const [hours, minutes] = time24.split(':');
    const hour = parseInt(hours);
    const ampm = hour >= 12 ? 'PM' : 'AM';
    const hour12 = hour % 12 || 12;
    return `${hour12}:${minutes} ${ampm}`;
  };

  // Initialize form with reservation data
  useEffect(() => {
    if (reservation) {
      const normalizedTime = normalizeTime(reservation.time);

      setFormData({
        date: reservation.date ? reservation.date.split('T')[0] : '',
        time: normalizedTime,
        guests: reservation.guests || 2,
        specialOccasion: reservation.specialOccasion || '',
        comments: reservation.comments || '',
        allergies: Array.isArray(reservation.allergies) ? reservation.allergies : [],
        dietaryPreference: Array.isArray(reservation.dietaryPreference) ? reservation.dietaryPreference : [],
      });
      setHasSpecialOccasion(!!reservation.specialOccasion);

      // Set meal period if exists
      if (reservation.mealPeriodId && availableMealPeriods.length > 0) {
        const mealPeriod = availableMealPeriods.find(mp => mp.id === reservation.mealPeriodId);
        if (mealPeriod) {
          setSelectedMealPeriod(mealPeriod);
        } else {
          setSelectedMealPeriod(availableMealPeriods[0]);
        }
      } else if (availableMealPeriods.length > 0) {
        setSelectedMealPeriod(availableMealPeriods[0]);
      }

      if (reservation.allergies?.length > 0) {
        setAllergiesExpanded(true);
      }
      if (reservation.dietaryPreference?.length > 0) {
        setDietaryExpanded(true);
      }
    }
  }, [reservation]);

  // Clear selected time if it's no longer available
  useEffect(() => {
    if (formData.time && !timeSlots.includes(formData.time)) {
      handleChange('time', '');
    }
  }, [formData.date, selectedMealPeriod, timeSlots]);

  // Get quick dates (next 30 days)
  const getQuickDates = () => {
    const dates = [];
    const today = new Date();

    for (let i = 0; i < 30; i++) {
      const date = new Date(today);
      date.setDate(today.getDate() + i);
      const weekday = date.toLocaleDateString('en-US', { weekday: 'short' });
      const monthDay = date.toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
      dates.push({
        weekday,
        monthDay,
        value: date.toISOString().split('T')[0],
      });
    }
    return dates;
  };

  const handleChange = (field, value) => {
    setFormData(prev => ({ ...prev, [field]: value }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError('');

    if (!formData.date || !formData.time || !formData.guests) {
      setError('Please fill in all required fields (date, time, and guests)');
      setLoading(false);
      return;
    }

    const selectedDate = new Date(formData.date);
    const today = new Date();
    today.setHours(0, 0, 0, 0);
    if (selectedDate < today) {
      setError('Please select a future date');
      setLoading(false);
      return;
    }

    try {
      const updateData = {
        date: formData.date,
        time: formData.time,
        guests: parseInt(formData.guests),
        specialOccasion: hasSpecialOccasion ? formData.specialOccasion : '',
        comments: formData.comments,
        allergies: formData.allergies,
        dietaryPreference: formData.dietaryPreference,
        ...(selectedMealPeriod && {
          mealPeriodId: selectedMealPeriod.id,
          mealPeriodLabel: selectedMealPeriod.label,
        }),
      };

      await updateReservation(reservation.id, updateData);
      onSuccess();
    } catch (err) {
      const errorMessage = err.response?.data?.message || 'Failed to update reservation. Please try again.';
      setError(errorMessage);
    } finally {
      setLoading(false);
    }
  };

  // Scroll handlers
  const scrollLeft = (ref) => {
    if (ref.current) {
      ref.current.scrollBy({ left: -150, behavior: 'smooth' });
    }
  };

  const scrollRight = (ref) => {
    if (ref.current) {
      ref.current.scrollBy({ left: 150, behavior: 'smooth' });
    }
  };

  // Close modal on escape key
  useEffect(() => {
    const handleEscape = (e) => {
      if (e.key === 'Escape') {
        onClose();
      }
    };
    document.addEventListener('keydown', handleEscape);
    return () => document.removeEventListener('keydown', handleEscape);
  }, [onClose]);

  // Prevent body scroll when modal is open
  useEffect(() => {
    document.body.style.overflow = 'hidden';
    return () => {
      document.body.style.overflow = 'unset';
    };
  }, []);

  // Format display date
  const formatDisplayDate = (dateStr) => {
    if (!dateStr) return '';
    const date = new Date(dateStr);
    return date.toLocaleDateString('en-US', {
      weekday: 'long',
      year: 'numeric',
      month: 'long',
      day: 'numeric'
    });
  };

  return (
    <div className="edit-modal-overlay" onClick={onClose}>
      <div className="edit-modal-container" onClick={(e) => e.stopPropagation()}>
        <div className="edit-modal-header">
          <h3>
            <i className="bi bi-pencil-square me-2"></i>
            Edit Reservation
          </h3>
          <button className="erm-close-btn" onClick={onClose} aria-label="Close">
            <i className="bi bi-x-lg"></i>
          </button>
        </div>

        {reservation?.restaurant && (
          <div className="edit-modal-restaurant-info">
            <i className="bi bi-geo-alt-fill me-2"></i>
            {reservation.restaurant.name}
          </div>
        )}

        {/* Current Reservation Summary */}
        <div className="erm-current-info">
          <div className="erm-current-title">Current Reservation</div>
          <div className="erm-current-details">
            <span><i className="bi bi-calendar3"></i> {formatDisplayDate(reservation?.date)}</span>
            <span><i className="bi bi-clock"></i> {formatTime(normalizeTime(reservation?.time))}</span>
            <span><i className="bi bi-people"></i> {reservation?.guests} {reservation?.guests === 1 ? 'Guest' : 'Guests'}</span>
          </div>
        </div>

        {error && (
          <div className="edit-modal-error" role="alert">
            <i className="bi bi-exclamation-triangle-fill me-2"></i>
            {error}
          </div>
        )}

        <form onSubmit={handleSubmit} className="edit-modal-form">
          {/* Date Selection */}
          <div className="erm-form-section">
            <label className="erm-form-label">
              <i className="bi bi-calendar3 me-2"></i>
              Select Date
              {formData.date && (
                <span className="erm-selected-value">{formatDisplayDate(formData.date)}</span>
              )}
            </label>
            <div className="erm-scroll-wrapper">
              <button type="button" className="erm-scroll-btn erm-scroll-left" onClick={() => scrollLeft(datesScrollRef)}>
                <i className="bi bi-chevron-left"></i>
              </button>
              <div className="erm-quick-dates-scroll" ref={datesScrollRef}>
                <div className="erm-quick-dates-container">
                  {getQuickDates().map((d, idx) => (
                    <button
                      key={idx}
                      type="button"
                      className={`erm-quick-date-pill ${formData.date === d.value ? 'active' : ''}`}
                      onClick={() => handleChange('date', d.value)}
                    >
                      <div className="erm-weekday">{d.weekday}</div>
                      <div className="erm-date">{d.monthDay}</div>
                    </button>
                  ))}
                </div>
              </div>
              <button type="button" className="erm-scroll-btn erm-scroll-right" onClick={() => scrollRight(datesScrollRef)}>
                <i className="bi bi-chevron-right"></i>
              </button>
            </div>
            <input
              type="date"
              className="erm-calendar-input"
              value={formData.date}
              onChange={(e) => handleChange('date', e.target.value)}
              min={new Date().toISOString().split('T')[0]}
            />
          </div>

          {/* Meal Period Selection */}
          {availableMealPeriods.length > 0 && (
            <div className="erm-form-section">
              <label className="erm-form-label">
                <i className="bi bi-cup-hot me-2"></i>
                Select Meal Period
              </label>
              <div className="erm-meal-tabs">
                {availableMealPeriods.map((period) => (
                  <button
                    key={period.id}
                    type="button"
                    className={`erm-meal-tab ${selectedMealPeriod?.id === period.id ? 'active' : ''}`}
                    onClick={() => {
                      setSelectedMealPeriod(period);
                      handleChange('time', ''); // Reset time when meal period changes
                    }}
                  >
                    {period.label}
                  </button>
                ))}
              </div>
            </div>
          )}

          {/* Time Selection */}
          <div className="erm-form-section">
            <label className="erm-form-label">
              <i className="bi bi-clock me-2"></i>
              Select Time
              {formData.time && (
                <span className="erm-selected-value">{formatTime(formData.time)}</span>
              )}
            </label>
            {timeSlots.length > 0 ? (
              <div className="erm-scroll-wrapper">
                <button type="button" className="erm-scroll-btn erm-scroll-left" onClick={() => scrollLeft(timeSlotsScrollRef)}>
                  <i className="bi bi-chevron-left"></i>
                </button>
                <div className="erm-time-slots-scroll" ref={timeSlotsScrollRef}>
                  <div className="erm-time-slots-container">
                    {timeSlots.map((time) => (
                      <button
                        key={time}
                        type="button"
                        className={`erm-time-pill ${formData.time === time ? 'active' : ''}`}
                        onClick={() => handleChange('time', time)}
                      >
                        {formatTime(time)}
                      </button>
                    ))}
                  </div>
                </div>
                <button type="button" className="erm-scroll-btn erm-scroll-right" onClick={() => scrollRight(timeSlotsScrollRef)}>
                  <i className="bi bi-chevron-right"></i>
                </button>
              </div>
            ) : (
              <p className="erm-no-slots">No time slots available for the selected date/meal period</p>
            )}
          </div>

          {/* Guest Count */}
          <div className="erm-form-section">
            <label className="erm-form-label">
              <i className="bi bi-people me-2"></i>
              Number of Guests
              <span className="erm-selected-value">{formData.guests} {formData.guests === 1 ? 'Guest' : 'Guests'}</span>
            </label>
            <div className="erm-scroll-wrapper">
              <button type="button" className="erm-scroll-btn erm-scroll-left" onClick={() => scrollLeft(guestsScrollRef)}>
                <i className="bi bi-chevron-left"></i>
              </button>
              <div className="erm-guests-scroll" ref={guestsScrollRef}>
                <div className="erm-guests-container">
                  {Array.from({ length: 30 }, (_, i) => i + 1).map((num) => (
                    <button
                      key={num}
                      type="button"
                      className={`erm-guest-pill ${formData.guests === num ? 'active' : ''}`}
                      onClick={() => handleChange('guests', num)}
                    >
                      {num}
                    </button>
                  ))}
                </div>
              </div>
              <button type="button" className="erm-scroll-btn erm-scroll-right" onClick={() => scrollRight(guestsScrollRef)}>
                <i className="bi bi-chevron-right"></i>
              </button>
            </div>
          </div>

          {/* Special Occasion */}
          <div className="erm-form-section">
            <div className="erm-special-occasion-check">
              <input
                type="checkbox"
                className="erm-custom-checkbox"
                id="editHasSpecialOccasion"
                checked={hasSpecialOccasion}
                onChange={(e) => {
                  setHasSpecialOccasion(e.target.checked);
                  if (!e.target.checked) {
                    handleChange('specialOccasion', '');
                  }
                }}
              />
              <label className="erm-checkbox-label" htmlFor="editHasSpecialOccasion">
                <i className="bi bi-star-fill me-2"></i>
                Special Occasion
              </label>
            </div>
            {hasSpecialOccasion && (
              <input
                type="text"
                className="erm-modern-input mt-2"
                value={formData.specialOccasion}
                onChange={(e) => handleChange('specialOccasion', e.target.value)}
                placeholder="e.g., Birthday, Anniversary..."
                maxLength={200}
              />
            )}
          </div>

          {/* Allergies */}
          <div className="erm-form-section">
            <div
              className="erm-collapsible-header"
              onClick={() => setAllergiesExpanded(!allergiesExpanded)}
            >
              <label className="erm-form-label erm-clickable">
                <i className={`bi bi-chevron-${allergiesExpanded ? 'down' : 'right'} me-2`}></i>
                Allergies
                {formData.allergies.length > 0 && (
                  <span className="erm-badge">{formData.allergies.length} selected</span>
                )}
              </label>
            </div>
            {allergiesExpanded && (
              <div className="erm-multi-select-group">
                {['Peanuts', 'Shellfish', 'Dairy', 'Gluten', 'Eggs', 'Soy', 'Tree Nuts', 'Fish', 'Other'].map((allergy) => (
                  <div key={allergy} className="erm-form-check">
                    <input
                      type="checkbox"
                      className="erm-form-check-input"
                      id={`edit-allergy-${allergy}`}
                      checked={formData.allergies.includes(allergy)}
                      onChange={(e) => {
                        const newAllergies = e.target.checked
                          ? [...formData.allergies, allergy]
                          : formData.allergies.filter((a) => a !== allergy);
                        handleChange('allergies', newAllergies);
                      }}
                    />
                    <label className="erm-form-check-label" htmlFor={`edit-allergy-${allergy}`}>
                      {allergy}
                    </label>
                  </div>
                ))}
              </div>
            )}
          </div>

          {/* Dietary Preferences */}
          <div className="erm-form-section">
            <div
              className="erm-collapsible-header"
              onClick={() => setDietaryExpanded(!dietaryExpanded)}
            >
              <label className="erm-form-label erm-clickable">
                <i className={`bi bi-chevron-${dietaryExpanded ? 'down' : 'right'} me-2`}></i>
                Dietary Preferences
                {formData.dietaryPreference.length > 0 && (
                  <span className="erm-badge">{formData.dietaryPreference.length} selected</span>
                )}
              </label>
            </div>
            {dietaryExpanded && (
              <div className="erm-multi-select-group">
                {['Vegetarian', 'Vegan', 'Halal', 'Pescatarian', 'Gluten-Free', 'Dairy-Free'].map((pref) => (
                  <div key={pref} className="erm-form-check">
                    <input
                      type="checkbox"
                      className="erm-form-check-input"
                      id={`edit-dietary-${pref}`}
                      checked={formData.dietaryPreference.includes(pref)}
                      onChange={(e) => {
                        const newPrefs = e.target.checked
                          ? [...formData.dietaryPreference, pref]
                          : formData.dietaryPreference.filter((p) => p !== pref);
                        handleChange('dietaryPreference', newPrefs);
                      }}
                    />
                    <label className="erm-form-check-label" htmlFor={`edit-dietary-${pref}`}>
                      {pref}
                    </label>
                  </div>
                ))}
              </div>
            )}
          </div>

          {/* Comments */}
          <div className="erm-form-section">
            <label className="erm-form-label">
              <i className="bi bi-chat-left-text me-2"></i>
              Additional Comments
            </label>
            <textarea
              className="erm-modern-textarea"
              rows="3"
              value={formData.comments}
              onChange={(e) => handleChange('comments', e.target.value)}
              placeholder="Any special requests or comments..."
            ></textarea>
          </div>

          {/* Action Buttons */}
          <div className="edit-modal-actions">
            <button type="button" className="btn-modal-cancel" onClick={onClose}>
              Cancel
            </button>
            <button type="submit" className="btn-modal-save" disabled={loading}>
              {loading ? (
                <>
                  <span className="spinner-border spinner-border-sm me-2"></span>
                  Saving...
                </>
              ) : (
                <>
                  <i className="bi bi-check-lg me-2"></i>
                  Save Changes
                </>
              )}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default EditReservationModal;
